/* eslint-disable react-refresh/only-export-components */

import React, { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000LUState } from "../u2000Core/reducer/formReducer/U2000LUState";
import type { U3125InitLU } from "./U3125Service";
import type { U3125InvoiceSearchRow } from "./U3125Service";
import { U2000_ORG_DISPLAY_TIMEZONE, u2000SetActiveDisplayTimezone } from "../u2000Core/tools/U2000DateTimeHelpers";

export type InitLU = U2000LUState["personalization"] &
  U3125InitLU & {
    launchSearch: boolean;
    hideSearch: boolean;
    nbRecordPerPage: number;
  };

export interface U3125ContextType {
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3125InvoiceSearchRow>;
}

interface U3125ContextProviderProps {
  initLU: InitLU;
  children: ReactNode;
}

const U3125Context = React.createContext<U3125ContextType | undefined>(undefined);

export const U3125ContextProvider: React.FC<U3125ContextProviderProps> = ({ initLU, children }) => {
  u2000SetActiveDisplayTimezone(initLU.organizationTimezone ?? U2000_ORG_DISPLAY_TIMEZONE);

  const U2000LUStateHandlers = useU2000LUStateHandlers({ initLU: initLU as InitLU & U2000LUState });
  const U2000SearchStateHandlers = useU2000SearchStateHandlers({ luName: "u3125", launchSearch: initLU.launchSearch, hideSearch: initLU.hideSearch });
  const U2000GridStateHandlers = useU2000GridStateHandlers<U3125InvoiceSearchRow>({ nbRecordPerPage: initLU.nbRecordPerPage ?? 50 });

  const contextValue = useMemo<U3125ContextType>(
    () => ({
      luStateHandlers: U2000LUStateHandlers,
      searchStateHandlers: U2000SearchStateHandlers,
      gridStateHandlers: U2000GridStateHandlers,
    }),
    [U2000GridStateHandlers, U2000SearchStateHandlers, U2000LUStateHandlers],
  );

  return <U3125Context.Provider value={contextValue}>{children}</U3125Context.Provider>;
};

export function useU3125Context(): U3125ContextType {
  const context = useContext(U3125Context);
  if (!context) {
    throw new Error("useU3125Context must be used within a U3125ContextProvider");
  }
  return context;
}
